import { Brand as ProtoBrand } from '@italist/grpc-proto/entities/brand_pb';
import { Department } from '@italist/grpc-proto/entities/department_pb';
import { Category } from '@italist/grpc-proto/entities/category_pb';
import { SearchProduct } from '@italist/grpc-proto/entities/search_pb';
import { Brand } from '../../models/brand';
import { Product } from '../../models/product';
import { ProductSnapshot } from '../../models/cart';
import Departments from '../../models/departments';
import { URLify } from './URLify';
import { getProductSearchCategories, isSnapshot, getProductFilteredCategories } from './product';

export const buildBrandURL = (brand: ProtoBrand.AsObject, department?: Department.AsObject): string => {
  const url = `/brands/${URLify(brand.name)}/${brand.id}${department ? `/${department.internalName}` : ''}`;
  return encodeURI(url);
};

const preferredDepartments = [
  Departments.getWomenInternalName(),
  Departments.getGirlsInternalName(),
  Departments.getBabyGirlsInternalName(),
];

const findPreferredDepartment = (departmentName: string, proposedDepartments: string[]): string => {
  if (proposedDepartments.length === 1) {
    return proposedDepartments[0];
  }
  if (proposedDepartments.includes(departmentName)) {
    return departmentName;
  }
  const selectedDepartment = preferredDepartments.find(department => proposedDepartments.includes(department));
  if (selectedDepartment) {
    return selectedDepartment;
  }
  return proposedDepartments[0];
};

const findPreferredCategories = (departmentName: string, categories: Category.AsObject[]): Category.AsObject[] => {
  const categoriesByDepartment = {};
  categories.forEach(category => {
    const array = categoriesByDepartment[category.department.internalName] || [];
    array.push(category);
    categoriesByDepartment[category.department.internalName] = array;
  });
  const departments = Object.keys(categoriesByDepartment);
  const selectedDepartment = findPreferredDepartment(departmentName, departments);
  return categoriesByDepartment[selectedDepartment];
};

export const buildSearchResultProductURL = (product: SearchProduct.AsObject, departmentName: string): string => {
  const preferredCategories = findPreferredCategories(departmentName, product.categoriesList);
  const urlTokens = preferredCategories
    .slice()
    .reverse()
    .slice(0, 5)
    .map(element => URLify(element.name));
  urlTokens.push(URLify(product.model));
  urlTokens.push(product.productId);
  urlTokens.push(product.productVersionId);
  urlTokens.push(URLify(product.brand));
  return encodeURI(`/${urlTokens.join('/')}`);
};

const isBrand = (brand: string | Brand): brand is Brand => {
  return (brand as Brand).name !== undefined;
};

const getBrandName = (product: Product | ProductSnapshot): string => {
  const { brand } = product;
  if (isBrand(brand)) {
    return brand.name;
  }
  return brand;
};

export const buildProductUrl = (product: Product | ProductSnapshot): string => {
  const brandName = getBrandName(product);
  const urlTokens = getProductFilteredCategories(product)
    .slice()
    .reverse()
    .slice(0, 5)
    .map(element => URLify(element.name));
  urlTokens.push(URLify(product.model));
  urlTokens.push(product.productId);
  urlTokens.push(product.productVersionId);
  urlTokens.push(URLify(brandName));
  return encodeURI(`/${urlTokens.join('/')}/`);
};

export const buildCategorySearchUrl = (product: Product | ProductSnapshot): string => {
  const brandId = isSnapshot(product) ? product.brandId : product.brand.id;
  const categoryPath = getProductSearchCategories(product);
  const category = categoryPath[categoryPath.length - 1];
  return encodeURI(`${category.url}?categories[]=${category.id}&brands[]=${brandId}`);
};

export const getTopLevelCategoryFromUrl = (departmentName: string, catPath: string): string => {
  switch (departmentName) {
    case 'woman':
    case 'women':
      return 'women';
    case 'man':
    case 'men':
      return 'men';
    case 'kids':
      if (catPath) {
        const cats = catPath.split('/');
        if (cats.length) {
          return cats[0];
        }
        return undefined;
      }
      return undefined;
    default:
      return undefined;
  }
};
